import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { KccBarChart } from "@/components/kcc/Charts";

const IMG = "https://images.unsplash.com/photo-1628487749130-2d41acb1802a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjb3BwZXIlMjBvcGVuJTIwcGl0JTIwbWluZXxlbnwwfHx8fDE3ODU2MDk5Njd8MA&ixlib=rb-4.1.0&q=85";

export default function Mining360() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/mining/fleet").then(({ data }) => setData(data)); }, []);

    const fleetByStatus = ["running", "idle", "maintenance", "down"].map((s) => ({
        name: s.charAt(0).toUpperCase() + s.slice(1),
        value: (data?.fleet || []).filter((f) => f.status === s).length,
    }));

    const shiftTot = (data?.shifts || []).slice(0, 14).reverse().map((s) => ({
        name: s.date.slice(5, 10) + " " + s.shift[0],
        ore: s.ore_t / 1000,
        waste: s.waste_t / 1000,
    }));

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Mining360"
                title="Mine Operations Intelligence"
                description="Open-pit and underground operations across Kov, Mashamba East and T17. Live fleet dispatch, drill and blast, cycle-time and reconciliation."
                image={IMG}
            />
            <KpiGrid kpis={data?.kpis || []} accent="copper" cols={4} />

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Fleet Status Distribution" accent="copper">
                    <KccBarChart data={fleetByStatus} dataKey="value" label="Units" color="#b87333" />
                </Panel>
                <Panel title="Shift Ore vs Waste (kt)" accent="cobalt" className="xl:col-span-2">
                    <KccBarChart data={shiftTot} dataKey="ore" label="Ore (kt)" color="#e88c4a" />
                </Panel>
            </div>

            <Panel title="Mobile Fleet Register" subtitle={`${(data?.fleet || []).length} units under dispatch`} accent="copper">
                <DataTable
                    columns={[
                        { key: "asset_tag", label: "Asset" },
                        { key: "type", label: "Type" },
                        { key: "model", label: "Model" },
                        { key: "location", label: "Location" },
                        { key: "operator", label: "Operator" },
                        { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        { key: "utilisation_pct", label: "Util %", render: (r) => `${r.utilisation_pct}%` },
                        { key: "availability_pct", label: "Avail %", render: (r) => `${r.availability_pct}%` },
                        { key: "engine_hours", label: "Engine h", render: (r) => r.engine_hours.toLocaleString() },
                    ]}
                    rows={(data?.fleet || []).slice(0, 30)}
                    testid="fleet-table"
                />
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel title="Recent Shifts" subtitle="Dispatch handovers" accent="cobalt">
                    <DataTable
                        columns={[
                            { key: "date", label: "Date", render: (r) => r.date.slice(0, 10) },
                            { key: "shift", label: "Shift" },
                            { key: "area", label: "Area" },
                            { key: "ore_t", label: "Ore (t)", render: (r) => r.ore_t.toLocaleString() },
                            { key: "waste_t", label: "Waste (t)", render: (r) => r.waste_t.toLocaleString() },
                            { key: "avg_cycle_min", label: "Cycle (min)" },
                            { key: "supervisor", label: "Supervisor" },
                        ]}
                        rows={(data?.shifts || []).slice(0, 12)}
                    />
                </Panel>
                <Panel title="Drill & Blast Register" subtitle="Latest 12 blasts" accent="amber">
                    <DataTable
                        columns={[
                            { key: "blast_no", label: "Blast" },
                            { key: "date", label: "Date", render: (r) => r.date.slice(0, 10) },
                            { key: "pit", label: "Pit" },
                            { key: "bench", label: "Bench" },
                            { key: "holes", label: "Holes" },
                            { key: "explosive_kg", label: "Explosive (kg)", render: (r) => r.explosive_kg.toLocaleString() },
                            { key: "vibration_mms", label: "Vib mm/s" },
                            { key: "status", label: "Status", render: (r) => <StatusChip status={statusToChip(r.status)}>{r.status}</StatusChip> },
                        ]}
                        rows={(data?.blasts || []).slice(0, 12)}
                    />
                </Panel>
            </div>
        </div>
    );
}
